/* ═══════════════════════════════════════════════════════════════
   custom-widget — runtime WidgetDef factory for a saved CustomWidget
   row (Project Center widget builder). Wraps the pure evaluator
   (widgetEvaluator.ts) + a fresh per-project fetch of the widget's
   source in the ONE chrome (WidgetFrame) every stock widget uses, so a
   user-built widget looks and behaves exactly like one shipped in the
   catalog. `renderEvalResult` is shared with CustomWidgetBuilder.tsx so
   the builder's live preview renders pixel-identical to the real card.

   Builder R2 adds: meter/line bodies, the icon + accent picked in the
   builder, and drills — a List row opens the source's row target
   (`ctx.onDrill`/`ctx.navigate`); a Bar/Line bucket click swaps the
   body to an in-widget `WList` of that bucket's rows (never a page-
   level filter — "robust across every source," T4b lead ruling), each
   keeping its own row drill, with a "← All" button back to the chart.
   ═══════════════════════════════════════════════════════════════ */
import React from 'react';
import { hasPermission } from '../../../../shared/permissions';
import type { CustomWidget, CustomWidgetForm, WidgetTone } from '../../../../shared/entities';
import { WidgetFrame } from '../../../components/widgets/WidgetFrame';
import type { WidgetForm as SkeletonForm } from '../../../components/widgets/WidgetFrame';
import { Stat } from '../../../components/widgets/Stat';
import { Meter } from '../../../components/widgets/Meter';
import { WList, type WListRow } from '../../../components/widgets/WList';
import { WChart } from '../../../components/widgets/WChart';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { iconFor } from '../../../lib/customWidgetIcons';
import { requiresFor, type WidgetSource } from '../../../lib/widgetSources';
import { evaluateWidget, rowsInBucket, type WidgetEvalResult } from '../../../lib/widgetEvaluator';
import { useSettledFetch } from './shared';
import { ContextMenuItem } from '../../../components/ui/context-menu';
import type { DashboardDrill, ProjectWidgetCtx, ProjectWidgetDef } from './context';
import type { DeepLink } from '../../../types/navigation';

/** One numeric formatter for Meter's value row — trims to 1 decimal only
 *  when the value isn't already a whole number. */
function fmtMeterNum(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

/** A row → display string for List/bucket bodies, joining the shape's
 *  picked columns (or, for a bucket drill-down with no column picks of
 *  its own, up to three of the source's fields). */
function joinFields(row: Record<string, unknown>, fields: string[]): string {
  return (
    fields
      .map((f) => (row[f] == null ? '' : String(row[f])))
      .filter(Boolean)
      .join(' · ') || '—'
  );
}

function toWListRows(
  rows: Record<string, unknown>[],
  fields: string[],
  onRowClick?: (row: Record<string, unknown>) => void
): WListRow[] {
  return rows.map((r, i) => ({
    id: String((r as Record<string, unknown>).id ?? i),
    primary: joinFields(r, fields),
    onClick: onRowClick ? () => onRowClick(r) : undefined,
  }));
}

/** Renders an already-evaluated result in the same body every stock
 *  widget uses (Stat / Meter / WList / WChart) — shared by the real card
 *  (CustomWidgetBody below) and the builder's live preview. Pure
 *  presentation: no fetching, no permission checks. The builder's preview
 *  passes only `accent` (no click handlers — a preview never drills). */
export function renderEvalResult(
  result: WidgetEvalResult,
  form: CustomWidgetForm,
  opts?: {
    accent?: WidgetTone;
    onRowClick?: (row: Record<string, unknown>) => void;
    onBucketClick?: (key: string) => void;
  }
): React.ReactNode {
  const tone = opts?.accent;
  if (result.kind === 'number') {
    return <Stat value={String(result.value)} label="" tone={tone} />;
  }
  if (result.kind === 'meter') {
    const remaining = Math.max(0, result.target - result.value);
    return (
      <Meter
        label=""
        planned={result.target}
        actual={result.value}
        values={{
          planned: fmtMeterNum(result.target),
          actual: fmtMeterNum(result.value),
          remaining: `${fmtMeterNum(remaining)} remaining`,
        }}
        tone={tone}
      />
    );
  }
  if (result.kind === 'list') {
    const fields = form.kind === 'list' ? form.fields : [];
    const rows = toWListRows(result.rows, fields, opts?.onRowClick);
    return <WList rows={rows} max={rows.length || 1} />;
  }
  if (result.kind === 'line') {
    const data = result.points.map((p) => ({ label: p.label, key: p.key, value: p.value }));
    return (
      <WChart
        kind="line"
        data={data}
        series={[{ key: 'value', label: 'Value', tone: tone ?? 'accent' }]}
        xKey="label"
        onPointClick={
          opts?.onBucketClick
            ? (_key, row) => opts.onBucketClick!(String((row as { key: string }).key))
            : undefined
        }
      />
    );
  }
  // bar
  return (
    <WChart
      kind="bar"
      data={result.data}
      series={[{ key: 'value', label: 'Value', tone: tone ?? 'accent' }]}
      xKey="label"
      onBarClick={opts?.onBucketClick ? (key) => opts.onBucketClick!(key) : undefined}
    />
  );
}

function formSkeleton(form: CustomWidgetForm): SkeletonForm {
  if (form.kind === 'number') return 'stat';
  if (form.kind === 'meter') return 'meter';
  if (form.kind === 'list') return 'list';
  return 'chart'; // bar | line
}

/** Cross-page (`DeepLink`, `view` discriminant) vs in-page (`DashboardDrill`,
 *  `kind` discriminant) — the two shapes a source's `drill.row` can return. */
function fireDrill(ctx: ProjectWidgetCtx, target: DashboardDrill | DeepLink | null) {
  if (!target) return;
  if ('kind' in target) ctx.onDrill(target);
  else ctx.navigate?.(target);
}

/** Up to 3 of the source's own fields, for the bucket drill-down's row
 *  display when the shape (bar/line) picked no display columns of its
 *  own. Skips the field the bucket is already grouped/bucketed by — no
 *  point repeating it on every row. */
function bucketDisplayFields(source: WidgetSource, exclude: string): string[] {
  return source.fields
    .map((f) => f.key)
    .filter((k) => k !== exclude)
    .slice(0, 3);
}

function CustomWidgetBody({
  ctx,
  widget,
  source,
  canManage,
  onEdit,
  onDuplicate,
  onRename,
  onDelete,
}: {
  ctx: ProjectWidgetCtx;
  widget: CustomWidget;
  source: WidgetSource;
  canManage: boolean;
  onEdit: (row: CustomWidget) => void;
  onDuplicate: (row: CustomWidget) => void;
  onRename: (row: CustomWidget, name: string) => void;
  onDelete: (row: CustomWidget) => void;
}) {
  const [rows, setRows] = React.useState<Record<string, unknown>[]>([]);
  const [drillBucket, setDrillBucket] = React.useState<string | null>(null);
  const [renaming, setRenaming] = React.useState(false);
  const [renameValue, setRenameValue] = React.useState(widget.name);

  const { status, reload } = useSettledFetch(
    () => [source.load(ctx.project.id)],
    (results) => {
      const r = results[0];
      setRows(r.status === 'fulfilled' ? (r.value as Record<string, unknown>[]) : []);
    },
    [ctx.project.id, source.id]
  );

  React.useEffect(() => {
    window.addEventListener('specforma-sync-complete', reload);
    return () => window.removeEventListener('specforma-sync-complete', reload);
  }, [reload]);

  // A different widget (or its definition changing under Edit) drops any
  // in-progress bucket drill — it no longer describes what's on screen.
  React.useEffect(() => {
    setDrillBucket(null);
  }, [widget.id, widget.definition]);

  const result = evaluateWidget(widget.definition, rows, source);
  const isEmpty =
    result.kind === 'list'
      ? result.total === 0
      : result.kind === 'bar'
        ? result.data.length === 0
        : result.kind === 'line'
          ? result.points.length === 0
          : rows.length === 0;
  const frameStatus =
    status === 'loading' ? 'loading' : status === 'error' ? 'error' : isEmpty ? 'empty' : 'ready';

  const canRowDrill = !!source.drill?.row;
  const handleRowClick = canRowDrill
    ? (row: Record<string, unknown>) => fireDrill(ctx, source.drill!.row!(row))
    : undefined;
  const isBucketed =
    widget.definition.form.kind === 'bar' || widget.definition.form.kind === 'line';
  const handleBucketClick = isBucketed ? (key: string) => setDrillBucket(key) : undefined;

  function commitRename() {
    const next = renameValue.trim();
    setRenaming(false);
    if (next && next !== widget.name) onRename(widget, next);
    else setRenameValue(widget.name);
  }

  const menu = canManage ? (
    <>
      <ContextMenuItem onSelect={() => onEdit(widget)}>Edit</ContextMenuItem>
      <ContextMenuItem onSelect={() => onDuplicate(widget)}>Duplicate</ContextMenuItem>
      <ContextMenuItem
        onSelect={() => {
          setRenameValue(widget.name);
          setRenaming(true);
        }}
      >
        Rename
      </ContextMenuItem>
      <ContextMenuItem onSelect={() => onDelete(widget)} className="text-danger">
        Delete
      </ContextMenuItem>
    </>
  ) : undefined;

  const title = renaming ? (
    <Input
      autoFocus
      value={renameValue}
      onChange={(e) => setRenameValue(e.target.value)}
      onBlur={commitRename}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commitRename();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          setRenameValue(widget.name);
          setRenaming(false);
        }
      }}
      style={{ height: 'var(--space-6)' }}
    />
  ) : (
    widget.name
  );

  return (
    <WidgetFrame
      title={title}
      icon={iconFor(widget.definition.icon)}
      iconTone={widget.definition.accent}
      status={frameStatus}
      menu={menu}
      form={formSkeleton(widget.definition.form)}
      empty={{ title: 'No matching data' }}
      error={{ message: 'Could not load this widget', onRetry: () => void reload() }}
    >
      {drillBucket != null ? (
        <div className="flex flex-col gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDrillBucket(null)}
            style={{ alignSelf: 'flex-start' }}
          >
            ← All
          </Button>
          <WList
            rows={toWListRows(
              rowsInBucket(widget.definition, rows, source, drillBucket),
              widget.definition.form.kind === 'bar'
                ? bucketDisplayFields(source, widget.definition.form.groupBy)
                : widget.definition.form.kind === 'line'
                  ? bucketDisplayFields(source, widget.definition.form.dateField)
                  : [],
              handleRowClick
            )}
            max={25}
          />
        </div>
      ) : (
        renderEvalResult(result, widget.definition.form, {
          accent: widget.definition.accent,
          onRowClick: handleRowClick,
          onBucketClick: handleBucketClick,
        })
      )}
    </WidgetFrame>
  );
}

/** Builds a `ProjectWidgetDef` for one saved CustomWidget row. `actions`
 *  is supplied by the host (Dashboard.tsx) so this factory never needs to
 *  know about the builder's open/edit state — the header menu just calls
 *  back into it. */
export function customWidgetDef(
  row: CustomWidget,
  source: WidgetSource,
  actions: {
    onEdit: (row: CustomWidget) => void;
    onDuplicate: (row: CustomWidget) => void;
    onRename: (row: CustomWidget, name: string) => void;
    onDelete: (row: CustomWidget) => void;
  }
): ProjectWidgetDef {
  // requiresFor returns one merged set: the source's base view tier
  // (OR-any — holding ANY one grants read access) plus, when the
  // definition touches a financial field, the financial gate(s) stacked
  // on top (AND-all). Split it back into WidgetDef's two gates so
  // visibleWidgets/WidgetGrid enforce both correctly.
  const allKeys = requiresFor(row.definition, source);
  const financialExtras = allKeys.filter((k) => !source.requires.includes(k));

  return {
    id: `custom:${row.id}`,
    title: row.name,
    requiresAny: source.requires,
    requires: financialExtras.length > 0 ? financialExtras : undefined,
    cols: { min: 3, max: 12 },
    custom: { ownerId: row.ownerId, shared: !!row.shared },
    render: (ctx: ProjectWidgetCtx) => {
      const memberId = ctx.authUser?.teamMemberId || ctx.authUser?.id;
      const isOwner = !!memberId && memberId === row.ownerId;
      // Final gate (mirrors server.js custom_widgets update/delete exactly,
      // Builder R2 T4a): an owner manages with EITHER edit tier or the
      // per-row `dashboards.delete` key (server's delete is own-row-only,
      // so holding it at all is enough for YOUR OWN row); a non-owner needs
      // the row to be shared AND hold `dashboards.share_views` — bare
      // `dashboards.delete` alone (T0 Finding 4) no longer grants anything
      // on someone else's widget. Builder actions are absent on a closed
      // project (Josh 2026-09-28: nothing touches a closed project's
      // surface, including its own dashboard config).
      const ownerManage =
        isOwner &&
        (hasPermission(ctx.userPermissions, 'dashboards.edit_own') ||
          hasPermission(ctx.userPermissions, 'dashboards.edit_any') ||
          hasPermission(ctx.userPermissions, 'dashboards.delete'));
      const sharedManage =
        !!row.shared && hasPermission(ctx.userPermissions, 'dashboards.share_views');
      const canManage = !ctx.locked && (ownerManage || sharedManage);
      return (
        <CustomWidgetBody
          ctx={ctx}
          widget={row}
          source={source}
          canManage={canManage}
          onEdit={actions.onEdit}
          onDuplicate={actions.onDuplicate}
          onRename={actions.onRename}
          onDelete={actions.onDelete}
        />
      );
    },
  };
}
